<template>
    <div class="course-description-wrapper">
        <div :class="customCss">
            <div class="flex flex-col gap-5 w-full md:w-2/3" v-if="!isSimpleType()">
                <p class="text-2xl font-extralight italic text-secondary">
                    {{ courseData?.title }}
                </p>

                <div class="flex gap-2 items-center text-gray-600">
                    <i class="pi pi-clock"></i>
                    {{ translate('last updated at', 'ucfirst') }} {{ courseData?.last_update }}.
                </div>

                <div class="flex gap-2 items-center text-gray-600" v-if="courseData?.courseLanguage">
                    <i class="pi pi-play-circle"></i>
                    {{ translate('video in', 'ucfirst') }}
                    <span class="flex gap-1 items-center font-medium">
                        {{ ucfirst(courseData.courseLanguage.getName()) }}
                        <img alt="flag"
                            :src="getOnPublic(`/flags/${courseData.courseLanguage.code === 'pt' ? 'br' : courseData.courseLanguage.code}.svg`)"
                            class="small-flag my-auto w-5 h-4 object-cover shadow-sm rounded-sm" />
                    </span>
                </div>

                <div class="flex gap-2 items-center text-gray-600" v-if="courseData?.courseLanguage">
                    <i class="pi pi-language"></i>
                    {{ translate('learn', 'ucfirst') }}
                    <span class="flex gap-1 items-center font-medium">
                        {{ ucfirst(courseData.teachedLanguage.getName()) }}
                        <img alt="flag"
                            :src="getOnPublic(`/flags/${courseData.teachedLanguage.code === 'pt' ? 'br' : courseData.teachedLanguage.code}.svg`)"
                            class="small-flag my-auto w-5 h-4 object-cover shadow-sm rounded-sm" />
                    </span>
                </div>
            </div>

            <Divider v-if="!isSimpleType()" />

            
            <p v-if="isSimpleType()" class="text-2xl font-extralight italic text-secondary">
                {{ courseData?.title }}
            </p>
            <CoursePhotoComponent :course="courseData" />
        </div>

        <template v-if="!isSimpleType()">

            <Divider />

            <div class="flex flex-col px-5 my-5 gap-5 tracking-wide" v-if="statistics">
                <p class="text-xl font-extralight text-secondary">
                    {{ translate('info', 'ucfirst') }}
                </p>

                <div class="flex gap-6">
                    <div class="flex flex-col border-r border-iron pr-6 last:border-none">
                        <span class="font-bold text-lg text-secondary">{{ statistics.videosNumber || 0 }}</span>
                        <span class="text-sm text-gray-500">{{ translate('classes', 'ucfirst') }}</span>
                    </div>
                    <div class="flex flex-col border-r border-iron pr-6 last:border-none">
                        <span class="font-bold text-lg text-secondary">{{ statistics.resoursesCount || 0 }}</span>
                        <span class="text-sm text-gray-500">{{ translate('materials', 'ucfirst') }}</span>
                    </div>
                    <div class="flex flex-col last:border-none">
                        <span class="font-bold text-lg text-secondary">{{ statistics.lessonsCount || 0 }}</span>
                        <span class="text-sm text-gray-500">{{ translate('lessons', 'ucfirst') }}</span>
                    </div>
                </div>
            </div>

            <Divider v-if="statistics" />

            <div class="flex flex-col px-5 my-5 gap-5 tracking-wide">
                <p class="text-xl font-extralight text-secondary">
                    {{ translate('description', 'ucfirst') }}
                </p>
                <p class="tracking-wide text-gray-700 leading-relaxed whitespace-pre-line">
                    {{ courseData?.description }}
                </p>
            </div>

            <Divider />

            <div class="flex flex-col px-5 my-5 gap-5 tracking-wide" v-if="instructor">
                <p class="text-xl font-extralight text-secondary">
                    {{ translate('instructor', 'ucfirst') }}
                </p>

                <div class="flex flex-wrap gap-5 items-center">
                    <Avatar :image="instructor.getPhoto()" size="xlarge" shape="circle" class="shadow" />
                    <div class="flex flex-col">
                        <span class="font-semibold text-lg text-secondary">{{ instructor.name }}</span>
                        <span class="text-gray-500 text-sm">{{ instructor.getTitleByLang() }}</span>
                    </div>
                </div>

                <div class="flex flex-wrap gap-3" v-if="instructor.getLinks && instructor.getLinks().length > 0">
                    <Avatar v-for="(link, index) of instructor.getLinks()" :key="index" size="large" :icon="link.icon"
                        class="cursor-pointer hover:opacity-75 transition-opacity bg-surface-100 border border-iron text-secondary"
                        @click="openExternalLink(link.url)" />
                </div>

                <p class="tracking-wide text-gray-600 italic">
                    {{ instructor.getDescriptionByLang() }}
                </p>
            </div>

            <Divider v-if="instructor" />

            <div class="flex flex-col px-5 my-5 gap-5 tracking-wide">
                <p class="text-xl font-extralight text-secondary" v-if="hasCategories">
                    {{ translate('categories', 'ucfirst') }}
                </p>
                <div class="flex flex-wrap gap-2" v-if="hasCategories">
                    <Tag severity="info" class="px-3 py-1 font-medium bg-purple-suit-forced-light"
                        v-for="(category, index) of data.categories" :key="index">
                        {{ ucfirst(category[userLang]) }}
                    </Tag>
                </div>
                <div v-else class="text-gray-400 italic text-sm px-5">
                    {{ translate('No categories registered.') }}
                </div>
            </div>

            <template v-if="data.modules && data.modules.length > 0">
                <section id="course-content" class="mt-10 border-t-2 border-carrot px-5">
                    <p class="text-lg pb-5 pt-3">
                        {{ translate('Course content:') }}
                    </p>
                    <div class="w-full bg-iron rounded overflow-hidden shadow-sm">
                        <TreeTable :value="data.modules">
                            <Column field="title" :header="translate('name', 'ucfirst')" expander style="width: 80%"></Column>
                            <Column field="time" :header="translate('duration', 'ucfirst')" style="width: 15%"></Column>
                        </TreeTable>
                    </div>
                </section>
            </template>

        </template>
    </div>
</template>

<script setup>
import { computed, defineProps, onMounted, reactive, watch } from 'vue';
import { Divider, Avatar } from 'primevue';
import Tag from 'primevue/tag';
import { ucfirst } from '@/composables/textUtils';
import { getOnPublic } from '@/composables/imageChecker';
import urlHandler from '@/composables/urlHandler';
import TreeTable from 'primevue/treetable';
import Column from 'primevue/column';
import CoursePhotoComponent from './CoursePhotoComponent.vue';
import { translate } from '@/composables/translate.js';

const props = defineProps({
    courseData: {
        type: Object,
        required: true,
        default: () => ({})
    },
    statistics: {
        type: Object,
        required: false,
        default: () => ({
            resoursesCount: 0,
            videosNumber: 0,
            lessonsCount: 0
        })
    },
    instructor: {
        type: Object,
        required: false,
        default: null
    },
    userLang: {
        type: String,
        required: false,
        default: 'pt'
    },
    moduleData: {
        type: Object,
        required: true,
        default: () => ({})
    },
    categoryData: {
        type: Array,
        required: true,
        default: () => ([])
    },
    type: {
        type: String,
        required: false,
        default: 'full'
    },
    customCss: {
        type: String,
        required: false,
        default: 'flex flex-wrap px-5 my-5 gap-5 tracking-wide justify-between'
    }
});

const data = reactive({
    course: null,
    modules: [],
    categories: []
});

onMounted(() => {
    data.course = props.courseData;
    data.modules = props.moduleData || [];
    data.categories = props.categoryData || [];
});

watch(() => props.courseData, (newValue) => {
    data.course = newValue;
},
    { deep: true }
);

watch(() => props.moduleData, (newValue) => {
    data.modules = newValue;
},
    { deep: true }
);

watch(() => props.categoryData, (newValue) => {
    data.categories = newValue;
},
    { deep: true }
);

const hasCategories = computed(() => {
    return Array.isArray(data.categories) && data.categories.length > 0;
});

const openExternalLink = (url) => {
    if (url) {
        urlHandler.openExternal(url);
    }
};

function isSimpleType() {
    return props.type == 'simple';
}
</script>

<style scoped>
.small-flag {
    max-width: 20px;
    display: inline-block;
}
</style>